import { onScopeDispose, type Ref } from "vue";
import type {
  MediaCategory,
  MediaItem,
  MediaRequestContext,
} from "@aswin.dev/media-library";
import type { MediaResult } from "@aswin.dev/types";

export interface UseCloudMediaLibraryOptions {
  onRequestMedia?: (context: MediaRequestContext) => Promise<MediaItem | null>;
  mediaLibraryOpen: Ref<boolean>;
  mediaLibraryAccept: Ref<MediaCategory[] | undefined>;
  /** When set, conversion size changes in the library update popup canvas width live. */
  onTemplateWidthPreview?: (width: number | undefined) => void;
}

export interface UseCloudMediaLibraryReturn {
  handleRequestMedia: () => Promise<MediaResult | null>;
  handleMediaSelect: (item: MediaItem, templateWidth?: number) => void;
  handleMediaLibraryClose: () => void;
  handleMediaTemplateWidthPreview: (templateWidth?: number) => void;
}

export function useCloudMediaLibrary(
  options: UseCloudMediaLibraryOptions,
): UseCloudMediaLibraryReturn {
  const {
    onRequestMedia,
    mediaLibraryOpen,
    mediaLibraryAccept,
    onTemplateWidthPreview,
  } = options;

  let mediaResolve: ((result: MediaResult | null) => void) | null = null;

  async function handleRequestMedia(): Promise<MediaResult | null> {
    // If consumer provides a custom media handler, use it
    if (onRequestMedia) {
      const item = await onRequestMedia({ accept: ["images"] });
      if (!item) return null;
      return { url: item.url, alt: item.alt_text || undefined };
    }

    // Otherwise open the built-in media library. If a previous request
    // is still pending (e.g. consumer fired two requests back-to-back),
    // settle it with null so its caller doesn't hang forever.
    if (mediaResolve) {
      mediaResolve(null);
      mediaResolve = null;
    }
    mediaLibraryAccept.value = ["images"];
    mediaLibraryOpen.value = true;
    return new Promise<MediaResult | null>((resolve) => {
      mediaResolve = (result) => {
        resolve(result);
      };
    });
  }

  function handleMediaSelect(item: MediaItem, templateWidth?: number): void {
    mediaLibraryOpen.value = false;
    mediaResolve?.({
      url: item.url,
      alt: item.alt_text || undefined,
      templateWidth,
    });
    mediaResolve = null;
  }

  function handleMediaTemplateWidthPreview(templateWidth?: number): void {
    onTemplateWidthPreview?.(templateWidth);
  }

  function handleMediaLibraryClose(): void {
    mediaLibraryOpen.value = false;
    mediaResolve?.(null);
    mediaResolve = null;
  }

  onScopeDispose(() => {
    if (mediaResolve) {
      mediaResolve(null);
      mediaResolve = null;
    }
  });

  return {
    handleRequestMedia,
    handleMediaSelect,
    handleMediaLibraryClose,
    handleMediaTemplateWidthPreview,
  };
}
